import { Injectable, inject } from '@angular/core';

import {
  EMPTY,
  NEVER,
  Observable,
  Subject,
  catchError,
  exhaustMap,
  filter,
  iif,
  map,
  mergeMap,
  of,
  shareReplay,
  startWith,
  switchMap,
  take,
  tap,
  throwError
} from 'rxjs';

import {
  AddToCartInput,
  AddToCartItemPayload,
  AddToCartPayload,
  CART_MESSAGES_TRANSLATIONS,
  Cart,
  CartItemInput,
  CartMessageData,
  CartMessageDataDto,
  CartMessageToastKey,
  FetchPolicy,
  Product,
  RemoveCartStatus,
  TOAST_MESSAGES_SERVICE,
  ToastOptions
} from '@hdms/shared/common-types';
import {
  CookieService,
  EnvironmentService,
  HashTriggerService,
  LogoutService,
  PlatformService,
  TabSyncService,
  TokenService,
  WindowRefService
} from '@hdms/shared/util-core';

import { CART_COOKIE_NAME } from '../constants';
import { getLocalization } from '../helper';
import { UserContextService } from '../user';

import { AddToCartGqlService } from './add-to-cart-gql.service';
import { CreateCartGqlService } from './create-cart-gql.service';
import { MiniCartGqlService } from './mini-cart-gql.service';
import { QuickAddToCartGqlService } from './quick-add-to-cart-gql.service';
import { RemoveCartGqlService } from './remove-cart-gql.service';

interface TabSyncCart {
  cart: Cart;
}

const CART_MESSAGE_OPTIONS: ToastOptions = {
  autoClose: true,
  timeout: 10000,
  tapToDismiss: false,
  showCloseButton: true,
  uniqueKey: CartMessageToastKey.PRIMARY
};

const ADD_TO_CART_MESSAGE_EVENT = 'hdmsAddToCartMessage';

const ADD_TO_CART_HASH = '#add-to-cart=.*';

// cart cookie lifetime = 14 days
const CART_COOKIE_TTL = 1209600;

const CART_DEFAULT: Cart = { numberOfItems: 0, id: null };

export function calculateItemsAddedTotalCount(items: AddToCartItemPayload[]): number {
  return items?.reduce((total, item) => total + (item.quantity || 1), 0) || 0;
}

export function hasRoundupItems(items: AddToCartItemPayload[]): boolean {
  return items?.some(item => item.hasAdjustedQuantity === true) || false;
}

export function createSuccessCartMessageData(items: AddToCartItemPayload[]): CartMessageData {
  const { title, thumbnail } = (items?.length === 1 && items[0].product) || ({} as Product);

  return {
    productTitle: title,
    image: thumbnail,
    quantity: calculateItemsAddedTotalCount(items),
    roundUp: hasRoundupItems(items),
    showCartButton: true
  };
}

export type AddToCartItems = Omit<AddToCartInput, 'cartId' | 'localization'>;

@Injectable({
  providedIn: 'root'
})
export class CartService {
  public readonly cart$: Observable<Cart>;
  public readonly cartAmount$: Observable<number>;

  private readonly env = inject(EnvironmentService);
  private readonly tokenService = inject(TokenService);
  private readonly cookie = inject(CookieService);
  private readonly platformService = inject(PlatformService);
  private readonly logoutService = inject(LogoutService);
  private readonly queryCartGqlService = inject(MiniCartGqlService);
  private readonly createCartGqlService = inject(CreateCartGqlService);
  private readonly addToCartGqlService = inject(AddToCartGqlService);
  private readonly quickAddToCartGqlService = inject(QuickAddToCartGqlService);
  private readonly removeCartGqlService = inject(RemoveCartGqlService);
  private readonly tabSyncService = inject(TabSyncService);
  private readonly userContext = inject(UserContextService);
  private readonly win = inject<WindowRefService>(WindowRefService).nativeWindow;
  private readonly envService = inject(EnvironmentService);
  private readonly hashTrigger = inject(HashTriggerService);

  private readonly toastMessagesService = inject(TOAST_MESSAGES_SERVICE, { optional: true });
  private readonly cartMessagesTranslations = inject(CART_MESSAGES_TRANSLATIONS, { optional: true });

  private readonly localization = getLocalization(this.envService);
  private readonly refreshCartSubject = new Subject<Cart>();

  constructor() {
    this.cart$ = this.getCart$();
    this.cartAmount$ = this.cart$.pipe(map(({ numberOfItems }) => numberOfItems));

    if (this.platformService.isBrowser) {
      this.listenToAddToCartMessageUpdates();
      this.listenToAddToCartHashTrigger();
    }
  }

  public get cartId(): string {
    return this.cookie.get<string>(CART_COOKIE_NAME);
  }

  private get shouldBlockRequest(): boolean {
    return this.platformService.isServer || (this.env.isStandalone && !this.tokenService.authToken);
  }

  /**
   * Trigger cart amount refresh
   */
  public refreshCart(): void {
    this.refreshCartSubject.next(null);
  }

  /**
   * Add items to cart with default purchase type, e.g. via Quick Item Entry.
   * Set `showMessages` to false if displaying messages should be handled outside the service.
   * @param items
   * @param showMessages
   */
  public quickAddToCart$(items: CartItemInput[], showMessages = true): Observable<AddToCartPayload> {
    const localization = this.localization;

    const mutation = this.quickAddToCartGqlService
      .mutate({ input: { items, cartId: this.cartId, localization }, localization })
      .pipe(map(({ data }) => data.quickAddToCart));

    return this.processAddToCartPayload$(mutation, showMessages);
  }

  /**
   * Add items to cart with mixed purchase types.
   * @param items
   */
  public addToCart$(items: AddToCartItems): Observable<AddToCartPayload> {
    const localization = this.localization;

    const mutation = this.addToCartGqlService
      .mutate({
        input: {
          cartId: this.cartId,
          localization,
          ...items
        },
        localization
      })
      .pipe(map(({ data }) => data.addToCart));

    return this.processAddToCartPayload$(mutation);
  }

  /**
   * For successfully added items show an addToCart-toast message.
   * In case of an error show an error message, even together with addToCart message.
   * @param cartData
   */
  public showAddToCartMessage(cartData: CartMessageData): void {
    const { error, quantity } = cartData;
    if (quantity) {
      this.toastMessagesService.success(CartMessageDataDto.create(cartData), null, CART_MESSAGE_OPTIONS);
    }

    if (error) {
      this.toastMessagesService.danger(error.text, error.headline, {
        ...CART_MESSAGE_OPTIONS,
        ...(quantity && { uniqueKey: CartMessageToastKey.SECONDARY })
      });
    }
  }

  /**
   * Reset cart by deleting the cookie and setting amount to 0
   */
  public reset(): void {
    if (this.cartId) {
      this.deleteCookie();
      this.refreshCartSubject.next(CART_DEFAULT);
    }
  }

  /**
   * Remove cart and reset state
   */
  public removeCart$(): Observable<RemoveCartStatus> {
    const cartId = this.cartId;
    return cartId
      ? this.removeCartGqlService.mutate({ input: { cartId } }).pipe(
          map(({ data }) => data.removeCart.status),
          tap(_ => this.reset())
        )
      : EMPTY;
  }

  private processAddToCartPayload$(
    obs: Observable<AddToCartPayload>,
    showMessages = true
  ): Observable<AddToCartPayload> {
    return obs.pipe(
      tap(({ cart, ...payloadItems }) => {
        this.updateState(cart);
        showMessages && this.prepareAddToCartMessage(payloadItems);
      }),
      catchError(err => {
        showMessages && this.showAddToCartMessage(this.createErrorCartMessageData());
        return throwError(() => err);
      })
    );
  }

  /**
   * Create a cart and set the cookie to the received guid for registered users only.
   * @private
   */
  private createCart$(): Observable<Cart> {
    if (this.shouldBlockRequest) {
      return NEVER;
    }
    if (!this.userContext.isRegistered) {
      return of(CART_DEFAULT);
    }

    return this.createCartGqlService
      .mutate({
        input: {
          localization: getLocalization(this.env)
        }
      })
      .pipe(
        map(({ data }) => data.createCart?.cart),
        tap(({ id }) => this.setCookie(id)),
        catchError(_ => of(CART_DEFAULT))
      );
  }

  private queryCart$(id: string): Observable<Cart> {
    if (this.shouldBlockRequest) {
      return NEVER;
    }
    return this.queryCartGqlService
      .fetch({ id }, { fetchPolicy: FetchPolicy.NETWORK_ONLY })
      .pipe(map(({ data }) => data.miniCart));
  }

  private getCart$(): Observable<Cart> {
    return this.initCart$().pipe(
      tap(this.syncState.bind(this)),
      switchMap(c =>
        this.tabSyncService.onMessage$<TabSyncCart>('cart').pipe(
          map<TabSyncCart, Cart>(({ cart }) => ({
            ...c,
            ...cart
          })),
          startWith(c)
        )
      ),
      switchMap(cart =>
        this.refreshCartSubject.pipe(
          exhaustMap(c => (c ? of(c) : this.queryCart$(this.cartId))),
          tap(this.syncState.bind(this)),
          startWith(cart)
        )
      ),
      shareReplay(1)
    );
  }

  /**
   * Create cart if logged in as a registered user and cookie doesn't exist or is invalid
   */
  private initCart$(): Observable<Cart> {
    this.cleanupOnLogout();

    return of(this.cartId).pipe(
      mergeMap(id =>
        iif(
          () => !!id,
          this.queryCart$(id).pipe(
            catchError(_ => {
              this.deleteCookie();
              return this.createCart$();
            })
          ),
          this.createCart$()
        )
      ),
      take(1)
    );
  }

  /**
   * Clean up cart cookie on logout
   * @private
   */
  private cleanupOnLogout(): void {
    this.platformService.isBrowser && this.logoutService.logout$().subscribe(() => this.deleteCookie());
  }

  private setCookie(id: string): void {
    this.cookie.set(CART_COOKIE_NAME, id, { secure: true, path: '/', 'Max-Age': CART_COOKIE_TTL });
  }

  private deleteCookie(): void {
    this.cookie.delete(CART_COOKIE_NAME);
  }

  private syncState(cart: Cart): void {
    this.tabSyncService.postMessage<TabSyncCart>({ cart });
  }

  private updateState(cart: Cart): void {
    this.setCookie(cart.id);
    this.refreshCartSubject.next(cart);
  }

  private prepareAddToCartMessage({ failedItems, successfulItems }: Omit<AddToCartPayload, 'cart'>): void {
    this.showAddToCartMessage({
      ...(failedItems?.length && this.createErrorCartMessageData()),
      ...createSuccessCartMessageData(successfulItems)
    });
  }

  private createErrorCartMessageData(): CartMessageData {
    const { headline, copy } = this.cartMessagesTranslations.genericError;
    return {
      error: {
        headline,
        text: copy
      }
    };
  }

  /**
   * Listen to add-to-cart updates from legacy in order to display the toast
   * @private
   */
  private listenToAddToCartMessageUpdates(): void {
    this.env.hostContext &&
      this.win.addEventListener(ADD_TO_CART_MESSAGE_EVENT, (e: CustomEvent<CartMessageData>) => {
        this.showAddToCartMessage(e.detail);
      });
  }

  /**
   * Listen to hash triggers, in order to add items to the cart,
   * e.g. triggered by links from marketing banners.
   * As of now, only one item with single quantity can be added.
   * @private
   */
  private listenToAddToCartHashTrigger(): void {
    this.hashTrigger.registerHash(ADD_TO_CART_HASH);
    const regex = new RegExp(ADD_TO_CART_HASH);
    this.hashTrigger.hashChange$
      .pipe(
        filter(hash => regex.test(hash)),
        map(hash => hash.replace(/^.*=/, '')),
        filter(Boolean),
        switchMap(productId => this.quickAddToCart$([{ productId, quantity: 1 }]).pipe(catchError(() => EMPTY)))
      )
      .subscribe();
  }
}
